




















































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import {
	FeedTank,
	FeedTankSensorTypeEnum,
	FeedTypeLevelMetricEnum,
	Sensor,
	Transceiver,
} from '@/api';
import { FeedTankCurrentState, feedSiloSvg } from '../../../../models';
import { FeedTankService } from '../../../../services';

import Toast from '@/components/display/toast.vue';
import InformationRow from '@/components/display/information-row.vue';

/** Gives each silo drawing its own clip path id when several are on the page. */
let siloClipPathCount = 0;

@Component({
	components: {
		InformationRow,
	},
})
export default class FeedTankVolumeDisplay extends Vue {
	@Prop({ required: true, type: Object })
	private feedTank!: FeedTank;

	@Watch('feedTank')
	public async onFeedTankChanged(): Promise<void> {
		this.currentState = await this.fetchCurrentState();
	}

	public currentState: FeedTankCurrentState | null = null;
	public loading: boolean = true;

	public readonly silo = feedSiloSvg;
	public readonly clipPathId = `feed-silo-clip-${++siloClipPathCount}`;

	private get percentFull(): number {
		return Math.min(100, Math.max(0, this.currentState?.percentFull ?? 0));
	}

	/** Top of the fill, from the bottom of the hopper (0%) up to the roof peak (100%). */
	public get fillY(): number {
		const { fillBottom, fillTop } = this.silo;
		return fillBottom - (this.percentFull / 100) * (fillBottom - fillTop);
	}

	/** Level label sits beside the silo at the fill line, kept inside the graphic when nearly full. */
	public get labelTopPercent(): number {
		return (Math.max(this.fillY, 16) / 512) * 100;
	}

	public get levelLabel(): string {
		if (this.isStrain()) {
			const weight = this.currentState?.weight;
			return weight != null ? `Weight: ${Math.round(weight)} kg` : 'Weight: Unknown';
		}
		return `Volume: ${this.percentFull.toFixed(2)}%`;
	}

	private isVolume(): boolean {
		return this.feedTank.feedtype?.levelMetric === FeedTypeLevelMetricEnum.Volume;
	}

	private isStrain(): boolean {
		return this.feedTank.sensorType === FeedTankSensorTypeEnum.Strain;
	}

	private getUnit(isVolume: boolean, value?: number | null): string {
		return isVolume ? 'L' : (value ?? 0) > 1000 ? 't' : 'kg';
	}

	private convertToTonnes(isVolume: boolean, value?: number | null): string | null {
		return (
			(isVolume
				? value?.toString()
				: value != null
				? (value * (value >= 1000 ? 0.001 : 1)).toFixed(1)
				: null) ?? null
		);
	}

	public get currentVolumeString(): string {
		if (this.isStrain()) return this.currentStrainWeightString;
		const isVolume = this.isVolume();
		const metric = isVolume ? this.currentState?.volume : this.currentState?.weight;
		if (metric != null) {
			return `${this.convertToTonnes(isVolume, metric)} ${this.getUnit(isVolume, metric)} - ${
				this.lastVolumeTime
			}`;
		}
		return 'Unknown';
	}

	/** Strain tanks report weight directly: show it in whole kg. */
	private get currentStrainWeightString(): string {
		const weight = this.currentState?.weight;
		return weight != null ? `${Math.round(weight)} kg` : 'Unknown';
	}

	public get lastUpdateTime(): string {
		if (this.currentState?.timestamp) {
			return Vue.filter('dateTimeDiff')(this.currentState.timestamp * 1000);
		}
		return 'Unknown';
	}

	public get lastVolumeTime(): string {
		if (this.currentState?.volumeTs) {
			return Vue.filter('dateTimeDiff')(this.currentState.volumeTs * 1000);
		}
		return 'Unknown';
	}

	public getEstimatedEmptyString(shortLong: 'long' | 'short'): string {
		const key = shortLong === 'short' ? 'shortEstimatedEmptyDate' : 'longEstimatedEmptyDate';
		const estimatedEmptyTs = this.currentState?.[key];
		if (typeof estimatedEmptyTs === 'number') {
			const date = new Date(estimatedEmptyTs * 1000);
			return `${Vue.filter('shortDate')(date)} (${Vue.filter('dateTimeDiff')(date)})`;
		}
		return 'Unknown';
	}

	public getDailyUsageString(shortLong: 'long' | 'short'): string {
		let usageKey = shortLong === 'short' ? 'shortDailyUse' : 'longDailyUse';
		if (this.isStrain()) {
			const weightUsage = this.currentState?.[`${usageKey}Weight` as keyof FeedTankCurrentState];
			return typeof weightUsage === 'number' ? `${Math.round(weightUsage)} kg per day ` : 'Unknown';
		}
		const isVolume = this.isVolume();
		if (!isVolume) usageKey += 'Weight';
		const usageValue =
			<number | null>this.currentState?.[usageKey as keyof FeedTankCurrentState] ?? null;
		if (usageValue != null) {
			const converted = Number(this.convertToTonnes(isVolume, usageValue));
			return `${Math.round(converted)} ${this.getUnit(isVolume, usageValue)} per day `;
		}
		return 'Unknown';
	}

	/** Most recent event date of the reference points that have in-use sensors (as on the corrections tab). */
	public get lastCalibrationString(): string {
		type ReferencePointRow = { event_timestamp?: string; is_in_use?: boolean; allocations?: unknown[] | null };
		const eventTimes = (<ReferencePointRow[]>(this.feedTank.referencepointswithtrashed ?? []))
			.filter(rp => rp.is_in_use && rp.allocations?.length)
			.map(rp => rp.event_timestamp)
			.filter((ts): ts is string => !!ts)
			.map(ts => new Date(ts).getTime())
			.filter(ts => !isNaN(ts));
		if (!eventTimes.length) return 'Unknown';
		const latest = Math.max(...eventTimes);
		const daysAgo = Math.max(0, Math.floor((Date.now() - latest) / 86400000));
		const daysAgoString = daysAgo === 0 ? 'today' : `${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`;
		return `${Vue.filter('shortDate')(new Date(latest))} (${daysAgoString})`;
	}

	public getLastRefillString(): string {
		const lastDeliveryTs = this.currentState?.lastDeliveryTimestamp;
		let lastDeliveryVol = this.currentState?.lastDeliveryVolume;
		const isVolume = this.isVolume();
		if (!isVolume) lastDeliveryVol = this.currentState?.lastDeliveryWeight;
		if (typeof lastDeliveryTs === 'number' && typeof lastDeliveryVol === 'number') {
			const date = new Date(lastDeliveryTs * 1000);
			return `${this.convertToTonnes(isVolume, lastDeliveryVol)} ${this.getUnit(
				isVolume,
				lastDeliveryVol,
			)} - ${Vue.filter('shortDate')(date)}`;
		}
		return 'Unknown';
	}

	private feedTankService: FeedTankService = new FeedTankService(this.feedTank);
	private toast: Toast = new Toast();

	public async created(): Promise<void> {
		this.currentState = await this.fetchCurrentState();
		setTimeout(() => (this.loading = false), 500);
	}

	private async fetchCurrentState(): Promise<FeedTankCurrentState | null> {
		const currentStateResponse = await this.feedTankService.getCurrentState(this.feedTank);
		if (currentStateResponse?.errorMessage) {
			this.toast.makeToast(currentStateResponse.errorMessage, 'Error', 'danger');
			return null;
		}
		return currentStateResponse.data ?? {};
	}
}
